import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MessageSquare, AlertCircle } from 'lucide-react';

export const ChangeRequestDialog = ({ 
  open, 
  onOpenChange, 
  onSubmit, 
  soNumber,
  isSubmitting = false 
}) => {
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!message.trim()) {
      setError('Please describe the changes you need');
      return;
    }
    if (message.trim().length < 10) {
      setError('Please provide more details (at least 10 characters)');
      return;
    }
    setError('');
    onSubmit(message.trim());
  };

  const handleClose = () => {
    setMessage('');
    setError('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-amber-600" />
            Request Changes
          </DialogTitle>
          <DialogDescription>
            Describe the changes you need for Sales Order <span className="font-medium">{soNumber}</span>. 
            Our team will review your request and get back to you.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="change-message" className="text-sm font-medium">
              What changes do you need?
            </Label>
            <Textarea
              id="change-message"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (error) setError('');
              }}
              placeholder="Please describe the changes you need. For example:
- Change dimensions for specific items
- Update fabric or color selection
- Modify quantities
- Change delivery address"
              rows={5}
              className="resize-none"
              data-testid="change-request-textarea"
            />
            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
            <p className="text-xs text-neutral-500">
              {message.length}/500 characters
            </p>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Submitting a change request will pause the approval process. 
              Our team will contact you to discuss the requested modifications.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting || !message.trim()}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="submit-change-request-btn"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeRequestDialog;
